/**
 * V2ScreenRegistry - Single source of truth for V2 StepNode screen data
 *
 * Responsibilities:
 * - Store FlowStepNode instances (with entityId = id)
 * - Provide CRUD operations (inherited from EntityRegistry)
 * - Notify listeners on changes (Observable pattern - inherited)
 * - Provide StepNode-specific utilities (registerMany, getAllIds, debug)
 *
 * Pattern: Instance-based, extends EntityRegistry<FlowStepNode>
 *
 * Usage:
 * ```typescript
 * import { v2ScreenRegistry } from '@modules/reactFlow/services/onboarding/V2ScreenRegistry';
 * v2ScreenRegistry.register(step);
 *
 * const unsubscribe = v2ScreenRegistry.subscribeGlobal(() => {
 *   console.log('Screen changed');
 * });
 * ```
 *
 * Sprint: schema-v2-migration, Sprint 2
 * Related: OnboardingScreenRegistry (V1 equivalent), EntityRegistry (base class)
 */

import type { FlowStepNode } from '@modules/reactFlow/adapters/V2ScreenAdapter';
import { EntityRegistry } from '@modules/reactFlow/services/shared/EntityRegistry';

/**
 * Instance-based registry for V2 StepNode instances
 *
 * Inherits CRUD operations and Observable pattern from EntityRegistry base class.
 * Key: entityId (which equals StepNode.id)
 */
export class V2ScreenRegistry extends EntityRegistry<FlowStepNode> {
  constructor() {
    super();
  }

  /**
   * Register multiple screens at once (convenience method)
   */
  public registerMany(steps: FlowStepNode[]): void {
    steps.forEach(step => this.register(step));
    console.log(`[V2ScreenRegistry] Registered ${steps.length} screens`);
  }

  /**
   * Get all screen IDs
   *
   * Returns entityId (PRIMARY KEY) for all screens in registry
   */
  public getAllIds(): string[] {
    return this.getAll().map(step => step.entityId).filter((id): id is string => id !== undefined);
  }

  /**
   * Update screen content (with notifications)
   *
   * Alias for update() with explicit intent - use this for updates that should
   * trigger node re-renders (name, content changes, etc.).
   */
  public updateContent(stepId: string, updates: Partial<FlowStepNode>): void {
    this.update(stepId, updates);
  }

  /**
   * Clear all screens
   */
  public clear(): void {
    super.clear();
    console.log('[V2ScreenRegistry] Cleared all screens');
  }

  /**
   * Get registry stats (debugging utility)
   */
  public getStats(): {
    totalScreens: number;
    screenIds: string[];
  } {
    return {
      totalScreens: this.getAll().length,
      screenIds: this.getAllIds(),
    };
  }

  /**
   * Debug: Log registry state
   */
  public debug(): void {
    const stats = this.getStats();
    console.log('[V2ScreenRegistry] Debug:', {
      ...stats,
      screens: this.getAll().map(s => ({
        id: s.id,
        entityId: s.entityId,
        name: s.name,
        screenType: s.screenType,
      })),
    });
  }
}

/**
 * Export a default instance for the V2 onboarding flow editor
 */
export const v2ScreenRegistry = new V2ScreenRegistry();
